import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {FormControl, Table, TableProps} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';
import {SortableWarehouseRow} from './components/SortableWarehouseRow';
import {TableColumn} from './components/TableColumn';

interface WarehousesTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId?: string;
}

export function WarehousesTab(props: WarehousesTabProps) {
  const warehousesPriority = useWatch({control: props.formApi.control, name: 'warehousesPriority'});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (active.id !== over?.id) {
      const oldIndex = warehousesPriority.findIndex((item) => item.warehouseId === active.id);
      const newIndex = warehousesPriority.findIndex((item) => item.warehouseId === over?.id);

      const newItems = arrayMove(warehousesPriority, oldIndex, newIndex);

      // Update warehouse priorities based on new order
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        warehousePriority: index + 1,
      }));

      props.formApi.setValue('warehousesPriority', updatedItems);
    }
  };

  const tableColumns: TableProps['columns'] = [
    {element: <TableColumn />, width: '5%'},
    {element: <TableColumn title={i18n.t('entity.warehouse.labels.priority')} />, width: '10%'},
    {
      element: <TableColumn title={i18n.t('entity.warehouse.labels.warehouseName')} />,
      width: '21%',
    },
    {
      element: <TableColumn title={i18n.t('entity.warehouse.labels.warehouseCode')} />,
      width: '21%',
    },
    {
      element: <TableColumn title={i18n.t('entity.warehouse.labels.authorizationProfile')} />,
      width: '21%',
    },
    {element: <TableColumn title={i18n.t('entity.warehouse.labels.branch')} />, width: '21%'},
  ] as const;

  return (
    <VStack height="70vh">
      <Box height="90%" overflowY="scroll">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table columns={tableColumns} variant="bordered" data-testid="warehouses-table">
            <SortableContext
              items={warehousesPriority.map((warehouse) => warehouse.warehouseId)}
              strategy={verticalListSortingStrategy}
            >
              {warehousesPriority.map((warehouse, index) => (
                <SortableWarehouseRow
                  key={warehouse.warehouseId}
                  warehouse={warehouse}
                  index={index}
                />
              ))}
            </SortableContext>
          </Table>
        </DndContext>
      </Box>

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </VStack>
  );
}
